import React, { useState } from 'react'
import { Grid, Box, makeStyles } from '@material-ui/core'
import Collapse from '@material-ui/core/Collapse'
import Breadcrumbs from '../components/Breadcrumbs'
import clsx from 'clsx'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const PROJECT_SUBMISSION_LINK = process.env.REACT_APP_PROJECT_SUBMISSION_LINK

const useStyles = makeStyles((theme) => ({
  aboutTop: {
    backgroundColor: `#FFF`,
    display: `flex`,
    flexDirection: `column`,
    padding: `2.5rem 5rem 1rem`,
    width: `100%`,
    [theme.breakpoints.up('md')]: {
      flexDirection: `row`,
      padding: `2.5rem 10rem 1rem`,
    },
    [theme.breakpoints.up('lg')]: {
      padding: `2.5rem 15rem 1rem`,
    },
    [theme.breakpoints.up('xl')]: {
      padding: `2.5rem 21.75rem 1rem`,
    },
  },
  aboutTopFirst: {
    borderBottom: `1px solid #EEE`,
  },
  aboutTopLeft: {
    flex: 2,
    paddingRight: `0`,
    [theme.breakpoints.up('md')]: {
      paddingRight: `10vw`,
    },
  },
  aboutTopRight: {
    alignItems: `flex-start`,
    display: `flex`,
    flexDirection: `column`,
    justifyContent: `center`,
    flex: 1,
  },
  aboutHeader: {
    color: `#1E2553`,
    fontSize: `2.5rem`,
    lineHeight: `3rem`,
  },
  aboutParagraph: {
    color: `#707070`,
    fontSize: `1.25rem`,
    lineHeight: `2.25rem`,
    paddingBottom: `2rem`,
  },
  contactHeader: {
    paddingBottom: `4rem`,
  },
  contactSubheader: {
    margin: 0,
    paddingBottom: `1rem`,
    textTransform: `uppercase`,
  },
  contactLink: {
    color: `#009ECC`,
    marginBottom: `2.5rem`,
  },
  faqBottomBox: {
    backgroundColor: `#FFF`,
    margin: `0 auto 2.5rem`,
    maxWidth: `92.5%`,
    width: `92.5%`,
    padding: `2rem 0`,
    [theme.breakpoints.up('sm')]: {
      margin: `0 auto 2.5rem`,
      maxWidth: `80%`,
      width: `80%`,
    },
    [theme.breakpoints.up('md')]: {
      margin: `0 auto 2.5rem`,
      maxWidth: `80rem`,
      width: `70%`,
    },
  },
  faqBottomBoxFirst: {
    marginTop: `2rem`,
  },
  faqBottomBoxLast: {
    marginBottom: `13.5rem`,
  },
  sectionHeader: {
    borderBottom: `1px solid #EEE`,
    color: `#1E2553`,
    fontSize: `2rem`,
    lineHeight: `2.4375rem`,
    margin: 0,
    padding: `0 4.25rem 2rem`,
  },
  sectionHeaderQuestion: {
    fontSize: `1.75rem !important`,
    lineHeight: `2.75rem !important`,
  },
  sectionContent: {
    padding: `1rem 4.25rem 0`,
    position: `relative`,
  },
  sectionContentHeader: {
    margin: 0,
    paddingBottom: `1rem`,
    textTransform: `uppercase`,
  },
  sectionContentLink: {
    display: `inline-block`,
    paddingBottom: `2.5rem`,
  },
  expandMoreIcon: {
    cursor: `pointer`,
    position: `absolute`,
    top: `1.75rem`,
    right: `1.5rem`,
    transition: `all 0.625s ease`,
  },
  expandMoreIconFlipped: {
    transform: `scaleY(-1)`,
  },
}))

const AboutPage = () => {
  const classes = useStyles()
  const allClosed = Array(8).fill(false)
  const [openQuestions, setOpenQuestions] = useState([...allClosed])
  const handleClick = (number) => {
    let temp = [...openQuestions]
    temp[number] = !temp[number]
    setOpenQuestions(temp)
  }

  return (
    <Grid container>
      <Breadcrumbs crumbs={[{ text: `About`, path: `/about` }]} />
      <Box className={clsx(classes.aboutTop, classes.aboutTopFirst)}>
        <Box className={classes.aboutTopLeft}>
          <h2 className={classes.aboutHeader}>Welcome to JeffQuest</h2>
          <p className={classes.aboutParagraph}>
            This application helps medical students, residents, fellows, and clinical nurses join
            active research projects aligning with their clinical interests and academic goals.
          </p>
          <p className={classes.aboutParagraph}>
            Quickly post, search, and apply for projects. Connect with clinical leaders, discover
            scientific insights, and advance your career. With JeffQuest, finding your next research
            venture is fast and seamless.
          </p>
        </Box>
        <Box className={classes.aboutTopRight}>
          <h2 className={classes.contactHeader}>Contact Information</h2>
          <h3 className={classes.contactSubheader}>Email Us</h3>
          <a href="mailto:JeffQuestSupport@jefferson.edu" className={classes.contactLink}>
            JeffQuestSupport@jefferson.edu
          </a>
          <h3 className={classes.contactSubheader}>Submit A Ticket</h3>
          <a
            href="https://jefferson.service-now.com/"
            className={classes.contactLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            ServiceNow
          </a>
          <h3 className={classes.contactSubheader}>Additonal Resources</h3>
          <a
            href="https://jefferson.service-now.com/"
            className={classes.contactLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Confluence
          </a>
        </Box>
      </Box>
      <Box className={classes.aboutTop}>
        <Box className={classes.aboutTopLeft}>
          <h2 className={classes.aboutHeader}>Frequently Asked Questions (FAQ)</h2>
        </Box>
        <Box className={classes.aboutTopRight}></Box>
      </Box>
      <Box className={clsx(classes.faqBottomBox, classes.faqBottomBoxFirst)}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            What if I don't see my department?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[0]} collapsedHeight={38}>
              <p>
                The Jefferson Research Portal is currently open to the following
                departments/divisions:
              </p>
              <ul>
                <li>Cardiology</li>
                <li>Clinical Nursing </li>
                <li>Gastroenterology </li>
                <li>Medical Oncology </li>
                <li>Rehabilitation Medicine </li>
              </ul>
              <p>
                The Research Portal will eventually expand to more departments at Jefferson. We’ll
                send you more information before the Research Portal rolls out!
              </p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[0] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(0)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={classes.faqBottomBox}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            Can I filter my search results?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[1]} collapsedHeight={38}>
              <p>Yes! You can filter by position, department, topic, and attending.</p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[1] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(1)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={classes.faqBottomBox}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            How do I post a project?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[2]} collapsedHeight={38}>
              <p>
                To post a project, fill out{' '}
                <a href={PROJECT_SUBMISSION_LINK} target="_blank" rel="noopener noreferrer">
                  this intake form
                </a>{' '}
                with information on the project leads, division, and responsibilities.
              </p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[2] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(2)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={classes.faqBottomBox}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            How do I remove a project?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[3]} collapsedHeight={38}>
              <p>
                If you want to remove a project, please send your request to
                corey.fischer@jefferson.edu and allow 24-48 hours for it to get taken down.
              </p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[3] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(3)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={classes.faqBottomBox}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            Does my research project count toward my scholarly inquiry?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[4]} collapsedHeight={38}>
              <p>
                It might! Your academic advisor can give you more information on what counts toward
                scholarly inquiry.
              </p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[4] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(4)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={classes.faqBottomBox}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            What does my position mean?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[5]} collapsedHeight={38}>
              <p>Your position refers to your current research position at Jefferson.</p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[5] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(5)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={classes.faqBottomBox}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            Can I post multiple research projects?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[6]} collapsedHeight={38}>
              <p>
                Yes. If you are running more than one research project, you can post all of them to
                the Research Portal.
              </p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[6] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(6)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
      <Box className={clsx(classes.faqBottomBox, classes.faqBottomBoxLast)}>
        <Box>
          <h3 className={clsx(classes.sectionHeader, classes.sectionHeaderQuestion)}>
            Will I be an author on my research project?
          </h3>
          <Box className={classes.sectionContent}>
            <Collapse in={openQuestions[7]} collapsedHeight={38}>
              <p>
                Maybe! After joining a research project, talk to the principal investigator about
                whether there’s an opportunity for authorship.
              </p>
            </Collapse>
            <ExpandMoreIcon
              className={clsx(
                classes.expandMoreIcon,
                openQuestions[7] && classes.expandMoreIconFlipped
              )}
              onClick={() => handleClick(7)}
            ></ExpandMoreIcon>
          </Box>
        </Box>
      </Box>
    </Grid>
  )
}

export default AboutPage
